/**
 * Study-data centralization: keep patients, trials, and incidental safety
 * forms durable and centralized in Supabase, mirroring the pattern already
 * proven by the audit log.
 *
 * Design
 *   - Supabase is the durable source of truth; localStorage stays as a fast
 *     synchronous cache so the existing (sync) component code is untouched.
 *   - Write-through: every local change dispatches an event the app already
 *     fires (`patientsUpdated`, `incidentalFormsUpdated`, `trialsUpdated`).
 *     `initStudyDataSync()` listens for those and upserts the collection to
 *     Supabase in the background.
 *   - Hydration: `hydrateStudyData()` pulls the caller's rows from Supabase on
 *     login and unions them into localStorage (LOCAL-WINS — a live local edit
 *     is never clobbered; a cleared browser re-hydrates its data).
 *   - Export: `fetchAllPatients/Trials/Forms()` read the COMPLETE set from
 *     Supabase (paginated) so the CVM/FDA package is device-independent.
 *
 * Everything is guarded by `isSupabaseConfigured` and wrapped so a Supabase
 * outage or a missing migration degrades to the previous localStorage-only
 * behaviour instead of breaking the app.
 */

import { supabase, isSupabaseConfigured } from "../lib/supabaseClient";
import { getCurrentUserEmail } from "./auth";

const PAGE_SIZE = 1000;

// The Supabase table names are prefixed `study_` because this project also
// hosts a legacy relational schema that already owns bare `patients`/`trials`
// tables with an incompatible shape. The prefix keeps the v2 jsonb store
// side-by-side with that legacy data instead of colliding with it. The
// localStorage cache keys are unchanged (the rest of the app reads them).
const COLLECTIONS = {
  patients: {
    table: "study_patients",
    storageKey: "patients",
    event: "patientsUpdated",
  },
  trials: {
    table: "study_trials",
    storageKey: "trials",
    event: "trialsUpdated",
    // Trials seeded before ids were stabilized were minted from `Date.now()`,
    // so the same study exists in Supabase under several ids. Collapse them by
    // their real identity (name + official flag) as they hydrate so no consumer
    // of the cache renders the study two or three times.
    canonicalKey: (t) =>
      t && t.name ? `${t.name}::${t.isOfficial !== false}` : null,
  },
  forms: {
    table: "incidental_forms",
    storageKey: "incidentalForms",
    event: "incidentalFormsUpdated",
  },
};

// While true, write-through push handlers are inert. Set during hydration so
// the events hydration dispatches (to refresh the UI) do not immediately push
// the just-pulled rows straight back to the server.
let suppressPush = false;
let initialized = false;

const readLocal = (storageKey) => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeLocal = (storageKey, list) => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(list));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`Study-data cache write failed for ${storageKey}`, err);
  }
};

// -------- Remote row mappers (app object -> table row) --------------------

const nowIso = () => new Date().toISOString();

const patientRow = (p, actor) => ({
  id: p.id,
  assigned_vet_email: p.assignedVetEmail || actor || null,
  trial_id: p.trialId || null,
  status: p.status || null,
  name: p.name || null,
  data: p,
  updated_at: nowIso(),
  updated_by: actor || null,
});

const trialRow = (t, actor) => ({
  id: String(t.id),
  name: t.name || null,
  status: t.status || null,
  is_official: t.isOfficial ?? null,
  data: t,
  updated_at: nowIso(),
  updated_by: actor || null,
});

const formRow = (f, actor) => {
  // The form components don't stamp a submitter, so attribute the safety form
  // to the signed-in user and persist it INTO the data blob too — otherwise the
  // exported <form @submittedBy> would be blank. Attribution of adverse events
  // and deviations is exactly what an FDA reviewer needs.
  const submittedBy = f.submittedBy || actor || null;
  return {
    id: String(f.id),
    patient_id: f.patientId ? String(f.patientId) : null,
    type: f.type || null,
    submitted_by: submittedBy,
    data: submittedBy ? { ...f, submittedBy } : f,
  };
};

// -------- Write-through (upsert) ------------------------------------------

const upsertPatients = async (list, actor) => {
  if (list.length === 0) return;
  const rows = list.filter((p) => p && p.id).map((p) => patientRow(p, actor));
  const { error } = await supabase
    .from(COLLECTIONS.patients.table)
    .upsert(rows, { onConflict: "id" });
  if (error) {
    // eslint-disable-next-line no-console
    console.warn("Patient remote sync failed:", error.message || error);
  }
};

const upsertTrials = async (list, actor) => {
  if (list.length === 0) return;
  const rows = list
    .filter((t) => t && t.id !== undefined && t.id !== null)
    .map((t) => trialRow(t, actor));
  const { error } = await supabase
    .from(COLLECTIONS.trials.table)
    .upsert(rows, { onConflict: "id" });
  if (error) {
    // eslint-disable-next-line no-console
    console.warn("Trial remote sync failed:", error.message || error);
  }
};

const insertForms = async (list, actor) => {
  if (list.length === 0) return;
  const rows = list
    .filter((f) => f && f.id !== undefined && f.id !== null)
    .map((f) => formRow(f, actor));
  // Safety forms are append-only (no UPDATE policy). ignoreDuplicates makes
  // this an INSERT ... ON CONFLICT DO NOTHING, so re-pushing the whole list on
  // each submit only writes genuinely new rows.
  const { error } = await supabase
    .from(COLLECTIONS.forms.table)
    .upsert(rows, { onConflict: "id", ignoreDuplicates: true });
  if (error) {
    // eslint-disable-next-line no-console
    console.warn("Form remote sync failed:", error.message || error);
  }
};

const pushCollection = (which) => {
  if (!isSupabaseConfigured || suppressPush) return;
  const actor = getCurrentUserEmail();
  if (!actor) return; // Not signed in: nothing to attribute / RLS would reject.
  const { storageKey } = COLLECTIONS[which];
  const list = readLocal(storageKey);
  const run =
    which === "patients"
      ? upsertPatients
      : which === "trials"
      ? upsertTrials
      : insertForms;
  // Fire-and-forget; never block the UI thread on the network.
  Promise.resolve()
    .then(() => run(list, actor))
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.warn(`Study-data push (${which}) threw:`, err);
    });
};

// -------- Full remote reads (for the export) ------------------------------

const fetchAll = async (table) => {
  if (!isSupabaseConfigured) return null;
  try {
    const all = [];
    for (let offset = 0; ; offset += PAGE_SIZE) {
      // eslint-disable-next-line no-await-in-loop
      const { data, error } = await supabase
        .from(table)
        .select("data")
        .range(offset, offset + PAGE_SIZE - 1);
      if (error) {
        // eslint-disable-next-line no-console
        console.warn(
          `Study-data full fetch (${table}) failed:`,
          error.message || error
        );
        return null;
      }
      const batch = data || [];
      all.push(...batch);
      if (batch.length < PAGE_SIZE) break;
    }
    return all.map((r) => r.data).filter(Boolean);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`Study-data full fetch (${table}) threw:`, err);
    return null;
  }
};

/**
 * Complete, device-independent reads used by the CVM export. Each returns the
 * full set the caller is allowed to see (admins see everything). Falls back to
 * the local cache when Supabase is unreachable or unconfigured so an export
 * never hard-fails.
 */
export const fetchAllPatients = async () =>
  (await fetchAll(COLLECTIONS.patients.table)) ?? readLocal("patients");
export const fetchAllTrials = async () =>
  (await fetchAll(COLLECTIONS.trials.table)) ?? readLocal("trials");
export const fetchAllForms = async () =>
  (await fetchAll(COLLECTIONS.forms.table)) ?? readLocal("incidentalForms");

// -------- Hydration (remote -> local, local-wins union) -------------------

// Collapse rows that are the SAME entity under different ids (see the trials
// `canonicalKey`). Keeps the earliest original and carries the highest
// enrollment so a real enrollment is never hidden. Items without a canonical
// key pass through untouched.
const dedupeByCanonical = (list, keyOf) => {
  const byKey = new Map();
  const passthrough = [];
  list.forEach((item) => {
    const key = keyOf(item);
    if (!key) {
      passthrough.push(item);
      return;
    }
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, item);
      return;
    }
    const existingTime = new Date(existing.createdAt || 0).getTime();
    const candidateTime = new Date(item.createdAt || 0).getTime();
    const keep = candidateTime < existingTime ? item : existing;
    const drop = keep === existing ? item : existing;
    keep.enrolledPatients = Math.max(
      keep.enrolledPatients || 0,
      drop.enrolledPatients || 0
    );
    byKey.set(key, keep);
  });
  return [...byKey.values(), ...passthrough];
};

const hydrateCollection = async (which) => {
  const { table, storageKey, event, canonicalKey } = COLLECTIONS[which];
  const remote = await fetchAll(table);
  if (!remote) return; // fetch failed or not configured; keep local as-is.

  const local = readLocal(storageKey);
  const byId = new Map();
  // Local first so local-wins on id conflicts (never clobber in-progress edits).
  local.forEach((item) => {
    if (item && item.id !== undefined && item.id !== null) {
      byId.set(String(item.id), item);
    }
  });
  let added = 0;
  remote.forEach((item) => {
    if (!item || item.id === undefined || item.id === null) return;
    const key = String(item.id);
    if (!byId.has(key)) {
      byId.set(key, item);
      added += 1;
    }
  });

  let merged = Array.from(byId.values());
  // Collapse same-entity duplicates (currently trials). This also cleans a
  // cache that already held duplicates from before this fix, even when the
  // remote pull added nothing new.
  if (canonicalKey) merged = dedupeByCanonical(merged, canonicalKey);

  // Write only when something actually changed: new remote rows, or duplicates
  // collapsed. (Without a canonicalKey this reduces to the original
  // `added === 0` guard, so patients/forms behaviour is unchanged.)
  if (added === 0 && merged.length === local.length) return;

  writeLocal(storageKey, merged);
  // Refresh any mounted views. suppressPush stops this from bouncing straight
  // back to the server as a redundant upsert.
  window.dispatchEvent(new CustomEvent(event, { detail: { hydrated: true } }));
};

/**
 * Pull the signed-in user's study data from Supabase into the local cache.
 * Safe to call on every login / session restore; it only adds rows that are
 * not already present locally.
 */
// One-time-ish backfill: push the rows the signed-in user OWNS up to Supabase.
// This carries pre-centralization local data (created before this device
// synced, or while offline) into the durable store so the CVM export is
// complete on first login — without churning or misattributing other users'
// rows (an admin holding everyone's data after hydration only re-pushes their
// own). Idempotent upserts, fire-and-forget.
const backfillOwnRows = (actor) => {
  if (!actor) return;
  const ownPatients = readLocal("patients").filter(
    (p) => p && p.id && (p.assignedVetEmail || actor) === actor
  );
  const ownForms = readLocal("incidentalForms").filter(
    (f) => f && f.id && (f.submittedBy || actor) === actor
  );
  Promise.resolve()
    .then(() => upsertPatients(ownPatients, actor))
    .catch(() => {});
  Promise.resolve()
    .then(() => insertForms(ownForms, actor))
    .catch(() => {});
};

export const hydrateStudyData = async () => {
  if (!isSupabaseConfigured) return;
  const actor = getCurrentUserEmail();
  if (!actor) return;
  suppressPush = true;
  try {
    await Promise.all([
      hydrateCollection("patients"),
      hydrateCollection("trials"),
      hydrateCollection("forms"),
    ]);
  } catch (err) {
    // Never let a hydration failure bubble into the auth flow that calls this.
    // eslint-disable-next-line no-console
    console.warn("Study-data hydration failed:", err);
  } finally {
    suppressPush = false;
  }
  // Carry any local-only rows the user owns up to the server.
  backfillOwnRows(actor);
};

/**
 * Attach the write-through listeners exactly once. Every subsequent
 * `patientsUpdated` / `trialsUpdated` / `incidentalFormsUpdated` event upserts
 * that collection to Supabase in the background.
 */
export const initStudyDataSync = () => {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  window.addEventListener("patientsUpdated", () => pushCollection("patients"));
  window.addEventListener("trialsUpdated", () => pushCollection("trials"));
  window.addEventListener("incidentalFormsUpdated", () =>
    pushCollection("forms")
  );
};
